import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Layers, Plus, MapPin, Package, Scissors } from "lucide-react";

const PALLET_MAX = 35;

export default function Pedane() {
    const [lotti, setLotti] = useState([]);   // lotti con residuo > 0
    const [ubic, setUbic] = useState([]);
    const [pedane, setPedane] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    // selection: { [lotto_id]: cartoni } (0/absent = non selezionato)
    const [sel, setSel] = useState({});
    const [ubicId, setUbicId] = useState("");
    const [note, setNote] = useState("");
    const [multi, setMulti] = useState(false); // default mono-lotto

    const load = async () => {
        setLoading(true);
        try {
            const [l, u, p] = await Promise.all([
                api.get("/lotti?disponibili=true"),
                api.get("/ubicazioni?tipo=magazzino"),
                api.get("/pedane"),
            ]);
            setLotti(l.data); setUbic(u.data); setPedane(p.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setSel({}); setUbicId(""); setNote(""); setMulti(false); setOpen(true); };

    const toggleLotto = (l) => {
        setSel(s => {
            const cur = s[l.id];
            if (cur > 0) {
                const n = { ...s }; delete n[l.id]; return n;
            }
            // se mono-lotto: azzera tutto e prendi solo questo
            if (!multi) return { [l.id]: l.cartoni_disponibili };
            return { ...s, [l.id]: l.cartoni_disponibili };
        });
    };

    const setCart = (l, v) => {
        const n = parseInt(v || 0);
        const max = l.cartoni_disponibili;
        setSel(s => ({ ...s, [l.id]: Math.min(Math.max(0, n), max) }));
    };

    const chosen = lotti.filter(l => (sel[l.id] || 0) > 0);
    const totale = chosen.reduce((s, l) => s + (sel[l.id] || 0), 0);
    const over = totale > PALLET_MAX;

    const submit = async () => {
        try {
            const body = {
                righe: chosen.map(l => ({ lotto_id: l.id, cartoni: sel[l.id] })),
                ubicazione_id: ubicId,
                note: note || null,
            };
            const { data } = await api.post("/pedane", body);
            toast.success(`Pedana ${data.numero} composta`);
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-blue-400/80">MAGAZZINIERE</div>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Pedane</h1>
                    <p className="mt-2 text-slate-400 text-sm max-w-2xl">
                        Componi pedane dai <span className="text-emerald-300">lotti operatore</span>. Default <span className="text-amber-300">mono-lotto</span>, attiva multi-lotto se necessario. Capacità suggerita <span className="font-mono">{PALLET_MAX}</span>.
                    </p>
                </div>
                <Button onClick={openNew} data-testid="button-new-pedana"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2" disabled={lotti.length === 0}>
                    <Plus size={16} /> Componi pedana
                </Button>
            </div>

            {lotti.length > 0 && (
                <Card className="card p-4 border-emerald-500/30 bg-emerald-500/5">
                    <div className="text-sm text-emerald-200">
                        <span className="font-bold">{lotti.length}</span> lotti con residuo (
                        <span className="font-mono">{lotti.reduce((s,l)=>s+l.cartoni_disponibili,0)}</span> cartoni disponibili)
                    </div>
                </Card>
            )}

            <Card className="card overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
                    <Layers size={16} className="text-blue-400" />
                    <h2 className="font-display font-bold text-lg">Pedane attive</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Numero</th>
                                <th className="text-left px-5 py-3">Ubicazione</th>
                                <th className="text-left px-5 py-3">Contenuto</th>
                                <th className="text-right px-5 py-3">Tot. cart.</th>
                                <th className="text-right px-5 py-3">Data</th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-pedane">
                            {loading && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && pedane.length === 0 && (
                                <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">
                                    <Layers size={36} className="mx-auto mb-3 opacity-40" />
                                    <div className="text-sm">Nessuna pedana attiva</div>
                                </td></tr>
                            )}
                            {pedane.map(p => (
                                <tr key={p.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`row-pedana-${p.numero}`}>
                                    <td className="px-5 py-3 doc-code text-xs">{p.numero}</td>
                                    <td className="px-5 py-3">
                                        {p.ubicazione && (
                                            <div className="flex items-center gap-2">
                                                <Badge className={`badge-pill ${p.tipo_ubicazione === "produzione" ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-blue-500/15 text-blue-300 border-blue-500/40"}`}>
                                                    {p.tipo_ubicazione?.toUpperCase()}
                                                </Badge>
                                                <span className="font-mono text-xs">{p.ubicazione.codice}</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-3">
                                        <div className="flex flex-wrap gap-1">
                                            {p.righe.map((r, i) => (
                                                <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                                                    {r.codice}×{r.cartoni}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono font-bold text-slate-100">{p.totale_cartoni}</td>
                                    <td className="px-5 py-3 text-right text-xs text-slate-500">{new Date(p.created_at).toLocaleString("it-IT")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
                    <DialogHeader><DialogTitle className="font-display">Componi Pedana</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs text-slate-400">Ubicazione destinazione (magazzino) *</Label>
                            <Select value={ubicId} onValueChange={setUbicId}>
                                <SelectTrigger data-testid="select-ubic-pedana" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue placeholder="Seleziona scaffale…" /></SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                    {ubic.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Nessuna ubicazione magazzino — creala prima</div>}
                                    {ubic.map(u => (
                                        <SelectItem key={u.id} value={u.id} data-testid={`opt-ubic-${u.codice}`}>
                                            <MapPin size={12} className="inline mr-1" />{u.codice}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                            <input type="checkbox" checked={multi}
                                   onChange={e => { setMulti(e.target.checked); if (!e.target.checked) setSel({}); }}
                                   data-testid="check-multi-lotto" className="rounded" />
                            <Scissors size={12} className="text-amber-400" />
                            Multi-lotto (permetti di combinare più lotti / cartoni parziali)
                        </label>

                        <div>
                            <Label className="text-xs text-slate-400">Lotti disponibili</Label>
                            <div className="mt-2 border border-slate-800 rounded-lg divide-y divide-slate-800 max-h-64 overflow-y-auto">
                                {lotti.map(l => {
                                    const on = (sel[l.id] || 0) > 0;
                                    return (
                                        <div key={l.id} data-testid={`row-lotto-${l.numero}`}
                                             className={`flex items-center gap-3 px-3 py-2 ${on ? "bg-amber-500/10" : "hover:bg-slate-800/50"}`}>
                                            <input type="checkbox" checked={on} onChange={() => toggleLotto(l)}
                                                   data-testid={`check-lotto-${l.numero}`} className="rounded" />
                                            <div className="flex-1 cursor-pointer" onClick={() => toggleLotto(l)}>
                                                <div className="flex items-center gap-2">
                                                    <span className="doc-code text-xs">{l.numero}</span>
                                                    <span className="font-mono text-xs text-amber-300">{l.codice}</span>
                                                    <span className="text-xs text-slate-400">{l.descrizione}</span>
                                                </div>
                                                <div className="text-[10px] text-slate-500">
                                                    op. {l.operatore_nome} · disp. <span className="text-emerald-300">{l.cartoni_disponibili}</span>/{l.cartoni}
                                                </div>
                                            </div>
                                            {on && multi ? (
                                                <Input type="number" min="1" max={l.cartoni_disponibili}
                                                       data-testid={`input-split-${l.numero}`}
                                                       value={sel[l.id]}
                                                       onChange={e => setCart(l, e.target.value)}
                                                       className="w-20 bg-slate-950/50 border-slate-800 h-8 text-sm font-mono text-right" />
                                            ) : (
                                                <div className="font-mono font-bold text-slate-100 w-20 text-right">{on ? sel[l.id] : l.cartoni_disponibili}</div>
                                            )}
                                        </div>
                                    );
                                })}
                                {lotti.length === 0 && <div className="px-3 py-4 text-xs text-slate-500 text-center">Nessun lotto disponibile</div>}
                            </div>
                        </div>

                        <div className="p-3 bg-slate-950/50 border border-slate-800 rounded flex justify-between items-center">
                            <div className="text-xs text-slate-400">Totale cartoni pedana</div>
                            <div className={`font-mono font-bold text-lg ${over ? "text-red-400" : totale === PALLET_MAX ? "text-emerald-400" : "text-slate-100"}`}>
                                {totale} / {PALLET_MAX}
                            </div>
                        </div>
                        {over && <div className="text-xs text-red-400">Capacità pedana suggerita superata. Puoi comunque procedere.</div>}

                        <div>
                            <Label className="text-xs text-slate-400">Note</Label>
                            <Textarea data-testid="input-pedana-note" value={note} onChange={e=>setNote(e.target.value)}
                                      className="bg-slate-950/50 border-slate-800 mt-1" rows={2} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                        <Button onClick={submit} data-testid="button-confirm-pedana"
                                disabled={!ubicId || chosen.length === 0}
                                className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                            <Package size={14} /> Crea Pedana & TP
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
